import type { ReactNode } from "react";
import { PocketRow, TaskStatusGlyph } from "@/components/ui";
import type { Task } from "@/api/tasks";
import { HighlightedTitle } from "@/components/panels/search-modal/highlighted-title";
import { useTaskPocketStore } from "@/stores/task-pocket-store";
import {
  taskAssigneeName,
  taskPullRequestText,
  taskStateText,
  taskStateTone,
} from "./task-state";
import { useTaskSignals } from "./use-task-signals";
import { shortTaskDueDate } from "./task-dates";
import { useBoardShowsProjects } from "./board-projects-context";
import { TaskProjectChip } from "./task-metadata-chips";
import { useAppLanguage } from "@/i18n";

interface ListViewRowProps {
  task: Task;
  onSelect: (taskId: string) => void;
  indent?: number;
  toggle?: ReactNode;
  /** A search whose first match in the title and details shows in bold. */
  query?: string;
}
export function ListViewRow({
  task,
  onSelect,
  indent = 0,
  toggle,
  query = "",
}: ListViewRowProps) {
  // The state, assignee, PR and date text below are translated on call.
  useAppLanguage();
  const signals = useTaskSignals();
  const tone = taskStateTone(task, {
    question: signals.questionTaskIds.has(task.id),
    running: signals.runningTaskIds.has(task.id),
  });
  const selected = useTaskPocketStore((state) => state.taskId === task.id);
  const showsProject = useBoardShowsProjects();
  const details = [
    task.ticket_display,
    taskAssigneeName(task),
    taskPullRequestText(task),
  ]
    .filter(Boolean)
    .join(", ");
  return (
    <div style={{ marginLeft: indent * 24 }} data-task-pocket-item={task.id}>
      <PocketRow
        testId={`list-view-row-${task.id}`}
        title={<HighlightedTitle text={task.title} query={query} />}
        leading={
          <span className="flex items-center gap-1">
            {toggle && <span className="relative z-10">{toggle}</span>}
            <TaskStatusGlyph status={task.status} size="md" neutral />
          </span>
        }
        secondLine={
          details || showsProject ? (
            <span className="inline-flex min-w-0 items-center gap-1.5">
              <TaskProjectChip projectId={task.project} />
              {details && <HighlightedTitle text={details} query={query} />}
            </span>
          ) : undefined
        }
        trailing={shortTaskDueDate(task.due_at)}
        status={
          tone
            ? {
                tone,
                text: (
                  <span className="sr-only">{taskStateText(task, tone)}</span>
                ),
              }
            : undefined
        }
        selected={selected}
        onSelect={() => onSelect(task.id)}
      />
    </div>
  );
}
